/**
 * Utility functions for making authenticated requests to the Plus Picklist API
 */

/**
 * Calculate HMAC for request authentication
 * @param {string} data - The request body as a string
 * @param {string} clientSecret - The Shopify client secret
 * @returns {Promise<string>} The calculated HMAC
 */
async function calculateHmac(data, clientSecret) {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(clientSecret),
        {
            name: 'HMAC',
            hash: 'SHA-256'
        },
        false,
        ['sign']
    );

    const signature = await crypto.subtle.sign(
        'HMAC',
        key,
        encoder.encode(data)
    );

    return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

/**
 * Add a product to a customer's picklist
 * @param {string} customerId - The Shopify customer ID
 * @param {string} productId - The Shopify product ID
 * @returns {Promise<Object>} The response from the API
 */
export async function addToPicklist(customerId, productId) {
    const clientSecret = 'd37c5de3273b2c8166e32d69ed05be20'; // Your client secret
    const shopDomain = 'plus-packaging.myshopify.com'; // Your shop domain

    const requestBody = {
        customer_id: customerId,
        product_id: productId
    };

    const data = JSON.stringify(requestBody);
    const timestamp = Math.floor(Date.now() / 1000);

    try {
        const hmac = await calculateHmac(data, clientSecret);

        const response = await fetch('https://plus-api.fifteen.dev/api/picklist/add', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Hmac-SHA256': hmac,
                'X-Shopify-Shop-Domain': shopDomain,
                'X-Shopify-Timestamp': timestamp.toString()
            },
            body: data
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error adding to picklist:', error);
        throw error;
    }
}

export async function removeFromPicklist(customerId, productId) {
    const clientSecret = 'd37c5de3273b2c8166e32d69ed05be20'; // Your client secret
    const shopDomain = 'plus-packaging.myshopify.com'; // Your shop domain 

    const requestBody = {
        customer_id: customerId,
        product_id: productId
    };

    const data = JSON.stringify(requestBody);
    const timestamp = Math.floor(Date.now() / 1000);

    try {
        const hmac = await calculateHmac(data, clientSecret);

        const response = await fetch('https://plus-api.fifteen.dev/api/picklist/remove', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Hmac-SHA256': hmac,
                'X-Shopify-Shop-Domain': shopDomain,
                'X-Shopify-Timestamp': timestamp.toString()
            },
            body: data
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error removing from picklist:', error);
        throw error;
    }
}

// Example usage:
// addToPicklist('gid://shopify/Customer/123456789', 'gid://shopify/Product/987654321')
//     .then(response => console.log('Success:', response))
//     .catch(error => console.error('Error:', error));
